/**
 * PageLoader Component
 *
 * A full-page loading component for page transitions and initial loads.
 */

import { type CSSProperties, type ReactNode } from 'react';
import { LoadingSpinner } from './LoadingSpinner';

/**
 * Props for PageLoader component
 */
export interface PageLoaderProps {
  /** Loading message to display */
  message?: string;
  /** Whether the loader is visible */
  loading?: boolean;
  /** Child content to show when not loading */
  children?: ReactNode;
  /** Background color */
  backgroundColor?: string;
  /** Minimum height (default: '100%') */
  minHeight?: string;
}

/**
 * PageLoader displays a centered loading spinner with optional message
 *
 * @example
 * ```tsx
 * <PageLoader loading={isLoading}>
 *   <MyContent />
 * </PageLoader>
 *
 * // Or standalone
 * {isLoading && <PageLoader message="Loading routes..." />}
 * ```
 */
export const PageLoader = ({
  message = 'Loading...',
  loading = true,
  children,
  backgroundColor = '#f9fafb',
  minHeight = '100%',
}: PageLoaderProps) => {
  // If not loading and has children, render children
  if (!loading && children) {
    return <>{children}</>;
  }

  // If not loading and no children, render nothing
  if (!loading) {
    return null;
  }

  return (
    <div style={{ ...containerStyle, backgroundColor, minHeight }} role="status" aria-live="polite">
      <div style={contentStyle}>
        <LoadingSpinner size="lg" />
        {message && <p style={messageStyle}>{message}</p>}
      </div>
    </div>
  );
};

/**
 * Component styles
 */
const containerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const contentStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
};

const messageStyle: CSSProperties = {
  margin: 0,
  fontSize: '1rem',
  color: '#6b7280',
  fontWeight: 500,
};

export default PageLoader;
